import { Badge, Text } from "@mantine/core";
import TripPickDoneButton from "../components/TripPickDoneButton/TripPickDoneButton";
import { AmountColumn, CreatedColumn, JourneyColumn, StatusColumn } from "./policyTableShared";
import { purchaseMethodLabel } from "./allPolicyHelpers";
import PolicyCellStack from "./PolicyCellStack";
import { coverTypeColor, purchaseColor } from "./policyTableUtils";
import { TRIP_PICK_ROLES } from "./allPolicyConstants";

const normalizeRoles = (roles) => {
  if (Array.isArray(roles)) return roles;
  if (roles) return [roles];
  return [];
};

const canViewTripPickField = (roles) =>
  normalizeRoles(roles).some((role) => TRIP_PICK_ROLES.includes(role));

const tripPickField = {
  key: "is_picked",
  label: "Booking Status",
  width: 130,
  stopRowClick: true,
  render: (row) => (
    <PolicyCellStack>
      <TripPickDoneButton
        tripId={row.id}
        isPicked={row.is_picked}
        status={row.status}
      />
    </PolicyCellStack>
  ),
};

const baseTripTableFields = [
  {
    key: "id",
    label: "#",
    width: 120,
    render: (row) => (
      <PolicyCellStack>
        <Text fw={700} c="orange.6" size="sm">#{row.id}</Text>
        {row.uuid ? <Text size="xs" c="dimmed" lineClamp={1}>{row.uuid}</Text> : null}
        {row.purchase_method ? (
          <Badge size="xs" variant="light" color={purchaseColor(row.purchase_method)} tt="uppercase">
            {purchaseMethodLabel(row.purchase_method)}
          </Badge>
        ) : null}
      </PolicyCellStack>
    ),
  },
  {
    key: "user_name",
    label: "CUSTOMER",
    width: 200,
    render: (row) => (
      <PolicyCellStack>
        <Text fw={600} size="sm" lineClamp={1}>
          {row.user_name || row.operator_profile_name || "—"}
        </Text>
        <Text size="xs" c="dimmed" lineClamp={1}>{row.email || "—"}</Text>
        {row.phone_number ? <Text size="xs" c="dimmed">{row.phone_number}</Text> : null}
      </PolicyCellStack>
    ),
  },
  {
    key: "cover_name",
    label: "COVER",
    width: 220,
    render: (row) => (
      <PolicyCellStack>
        <Text fw={600} size="sm" lineClamp={2}>{row.cover_name || "—"}</Text>
        {row.cover_type ? (
          <Badge size="xs" variant="light" color={coverTypeColor(row.cover_type)}>
            {row.cover_type}
          </Badge>
        ) : null}
      </PolicyCellStack>
    ),
  },
  { key: "traveller_from_name", label: "JOURNEY", width: 210, render: (row) => <JourneyColumn row={row} /> },
  { key: "status", label: "STATUS", width: 100, render: (row) => <StatusColumn row={row} /> },
  {
    key: "tour_operator_name",
    label: "OPERATOR",
    width: 160,
    render: (row) => (
      <PolicyCellStack>
        <Text fw={600} size="sm" lineClamp={1}>{row.tour_operator_name || "—"}</Text>
        <Text size="xs" c="dimmed">{row.operator_phone_number || row.tour_operator_phone || "—"}</Text>
      </PolicyCellStack>
    ),
  },
  { key: "total_amount", label: "AMOUNT", width: 110, render: (row) => <AmountColumn row={row} /> },
  {
    key: "no_of_pax",
    label: "PAX",
    width: 70,
    render: (row) => (
      <PolicyCellStack>
        <Text fw={700} size="lg" lh={1}>{row.no_of_pax ?? "—"}</Text>
        <Text size="xs" c="dimmed">pax</Text>
      </PolicyCellStack>
    ),
  },
  { key: "created_on", label: "CREATED", width: 100, render: (row) => <CreatedColumn row={row} /> },
];

export const buildTripTableFields = (roles = []) => {
  if (!canViewTripPickField(roles)) {
    return baseTripTableFields;
  }

  const [idField, ...restFields] = baseTripTableFields;
  return [idField, tripPickField, ...restFields];
};
